<script lang="ts">
	import type { Page } from "@sveltejs/kit";
	import type { Snippet } from "svelte";

	import { PUBLIC_BUILD_VERSION_SOURCE, PUBLIC_BUILD_VERSION_SOURCE_TYPE } from "$env/static/public";
	import { parseObjectValuesToString, templatePathSlugify } from "$lib/util/format";
	import { LOCATION_PROPS, streamOptimisticLocation } from "$lib/serv/locations";
	import { KEY_VERTICAL, Vertical, verticals } from "$lib/def/verticals";
	import ToolTip, { ToolPosition } from "../shared/ToolTip.svelte";
	import { observe } from "$lib/serv/core/local-store";
  import { KEY_SEARCH } from "$lib/serv/food-landing";
	import { query, queryFrom } from "$lib/util/url";
	import { fly, slide } from "svelte/transition";
	import { profile$ } from "$lib/serv/core/auth";
	import { BehaviorSubject, map } from "rxjs";

	import Modal from "../shared/Modal.svelte";

	type Props = {
		page: Page;
		open?: boolean;
		auth?: (signin: boolean) => void
		categories?: Snippet,
		children?: Snippet<[{ close: () => void }]>,
		header?: Snippet,
		footer?: Snippet,
	}

	let {
		page,
		open = $bindable(false),
		auth = () => {},
		categories,
		children,
		header,
		footer,
	}: Props = $props();

	const subjPage = new BehaviorSubject<Page>(page);
	const page$ = subjPage.asObservable();

	$effect(() => {
		subjPage.next(page);
	});
	
	const location$ = streamOptimisticLocation(page$.pipe(map(p => p.route.id)), profile$);
	const dev$ = observe<boolean>('dev-mode').pipe(map(({value:v}) => v));

	let showDebug = false;
	let ver = PUBLIC_BUILD_VERSION_SOURCE_TYPE === 'tag' ? PUBLIC_BUILD_VERSION_SOURCE : 'Dev Tools';

	const signedInProfileLinks: { name: string, link: string }[] = [
		{ name: 'Orders', link: '/profile/orders' },
		{ name: 'Profile & Addresses', link: '/profile' },
		{ name: 'Saved Cards', link: '/profile/cards' },
		{ name: 'SnackMe Gifts', link: '/about/snackme' },
		{ name: 'Pick n Pay Smartshopper', link: '/profile/smartshopper' },
		{ name: 'TakealotMORE', link: '/about/takealotmore' },
		{ name: 'Credit Wallet', link: '/profile/wallet' },
	];

	const links_one: { name: string, link: string }[] = [
		{ name: 'List your business on Mr D', link: '/about/become-a-partner' },
		{ name: 'Become a driver', link: '/about/become-a-driver' },
		{ name: 'Careers', link: '/about/careers' },
		{ name: 'Contact us', link: '/help' },
		{ name: 'Help', link: '/help' },
		{ name: "FAQ's", link: '/help/faqs' },
	];

	const links_two: { name: string, link: string, newTab?: boolean }[] = [
		{ name: 'Ideas & Complaints', link: '/help?order=null' },
		{ name: 'Responsible Disclosure Policy', link: 'https://www.mrdfood.com/responsible-disclosure-policy', newTab: true }, // Still old website link
		{ name: 'Privacy Policy', link: 'https://www.mrdfood.com/privacy-policy', newTab: true }, // Still old website link
		{ name: 'Terms & Conditions', link: '/about/terms' },
		{ name: 'Human Rights Statement', link: 'https://www.mrdfood.com/human-rights-statement', newTab: true }, // Still old website link
		{ name: 'Speak Up Process', link: 'https://www.mrdfood.com/speak-up-process', newTab: true }, // Still old website link
	];

</script>

{#if open}
	<button onclick={() => open = false} class="modal-background z-index-4" aria-label="close"></button>
{/if}

<nav class:open transition:slide={{ axis: 'x', duration: 200 }}>

	<section class="text-right exit-bar">
		<button class="close on-drawer" onclick={() => open = false} data-testid="close">
			<span class="flex icon-close-28"></span>
			<ToolTip message="Close" pos={ToolPosition.Left} />
		</button>
	</section>

	{#if header}
		<section onclick={() => open = false}>
			{@render header()}
		</section>
	{:else}
		<section class="verticals" class:desktop-only={query(page.url).has(KEY_SEARCH)}>
			{#each verticals as vertical, i}
				<!-- {@const params = parseObjectValuesToString($browsingLocation$)} -->
				{@const params = parseObjectValuesToString($location$)}
				{@const href = queryFrom(params).keep(...LOCATION_PROPS).set('vertical', vertical.slug).toUrl('/')}
				<a {href} class:active={query(page.url).hasValue(KEY_VERTICAL, vertical.slug)} in:fly={{ x: 20, delay: i * 20 }} out:fly={{ x: -20, delay: i * 20 }}>
					<div><svelte:component this={vertical.icon} /></div>
					<span>{vertical.title}</span>
				</a>
			{/each}
		</section>
	{/if}

	<article>
		{#if children}
			<section>
				{@render children({ close: () => { open = false } })}
				{#if $dev$}
					<button class="label-xl book ellipses text-left" style="margin-top: 0; width: 100%;" onclick={e => showDebug = true}>{ver}</button>
				{/if}
			</section>
		{:else}

			<section>
				{#if !$profile$}
						<button class="label-xl medium ellipses text-left" style="margin-top: 0; width: 100%;" onclick={() => auth(true)}>Sign in</button>
				{:else}

					{#each signedInProfileLinks as item, i}
						{@const active = `${item.link}/` === page.url.pathname || item.link === page.url.pathname}
						<a href={item.link} class="label-xl medium ellipses" class:active in:fly={{ x: 20, delay: i * 20 }} out:fly|global={{ x: -20, delay: i * 20 }}>{item.name}</a>
					{/each}

					<button class="label-xl medium ellipses text-left" style="margin-top: 0; width: 100%;" onclick={() => auth(false)} data-testid="logout">Sign out</button>
				
				{/if}
				
				{#if $dev$}
					<button class="label-xl book ellipses text-left" style="margin-top: 0; width: 100%;" onclick={e => showDebug = true}>{ver}</button>
				{/if}
			</section>

			{#if footer}
				<section>
					{@render footer()}
				</section>
			{/if}

			<section>
				{#each links_one as item, i}
				{@const active = `${item.link}/` === page.url.pathname || item.link === page.url.pathname}
					<a href={item.link} class="label-m book ellipses grey" class:active in:fly={{ x: 20, delay: i * 20 }} out:fly|global={{ x: -20, delay: i * 20 }}>{item.name}</a>
				{/each}
			</section>

			<section>
				{#each links_two as item, i}
				{@const active = `${item.link}/` === page.url.pathname || item.link === page.url.pathname}
					<a href={item.link} target={item.newTab ? '_blank' : '_self'} class="label-m book ellipses grey" class:active in:fly={{ x: 20, delay: i * 20 }} out:fly|global={{ x: -20, delay: i * 20 }}>{item.name}</a>
				{/each}
			</section>

		{/if}

	</article>

	{#if categories && !query(page.url).has(KEY_SEARCH)}
		<div class="float-button" class:on-groc={query(page.url).hasValue(KEY_VERTICAL, Vertical.Grocery)} onclick={() => open = true}>Categories <span class="icon-chevron-up-20"></span></div>
	{/if}
</nav>

{#if showDebug}
	<Modal on:close={() => showDebug = false}>
		{#await import('$lib/ui/profile/Debug.svelte')}
			Please wait...
		{:then Debug}
			<Debug.default />
		{:catch error}
			Failed to load component
		{/await}
	</Modal>
{/if}

<svelte:body style={categories && !query(page.url).has(KEY_SEARCH) ? '--bottom: 0px' : ''} />

<!-- {#if $$slots.categories && !query(page.url).has(KEY_SEARCH)}
	<style>
		:global(body) {
			--layout-footer-height: 0px;
		}
	</style>
{/if} -->